import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import qs from 'qs';
import AutoSizer from 'react-virtualized-auto-sizer';

import { app, media } from 'context';
import { GRAY_50_HEX } from 'shared';

import FloatingBulkActions from './FloatingBulkActions';
import Row from './Row';
import TableFooter from './TableFooter';
import HeaderRow from './TableHeaders/HeaderRow';
import TableControlsHeader from './TableHeaders/TableControlsHeader';
import { BULK_SELECT_COLUMN_KEY, CELL_HEIGHT, CELL_PADDING } from './constants';
import {
  Column,
  ColumnWidths,
  DefaultFilterState,
  DefaultSortState,
  FilterState,
  Pagination,
  Row as RowType,
  RowBackgroundSurface,
  SetColumnWidths,
  SortState
} from './types';
import { filterRows } from './utils';
import Button from '../Button';
import IconButton from '../IconButton';
import Layout from '../Layout';
import { TABLE_FILTER_SORT_HEIGHT_MOBILE, TABLE_TITLE_HEIGHT_MOBILE } from '../PageLayout/constants';
import Skeleton from '../Skeleton';
import { sortRows } from '../Table/utils';
import Text from '../Text';
import { BOTTOM_NAVIGATION_HEIGHT, NEW_TABLE_HEADER_HEIGHT } from '../constants';
import { lockScreenScroll, unlockScreenScroll } from '../utils';

function NewTableAutoSizer({
  shrinkToContent,
  children
}: {
  shrinkToContent: boolean;
  children: (args: { width: number }) => React.ReactNode;
}) {
  if (shrinkToContent) {
    return <AutoSizer disableHeight>{children}</AutoSizer>;
  }

  return <AutoSizer>{children}</AutoSizer>;
}

export interface Props<T extends RowType> {
  nonPaginatedRows?: T[];
  columns: Column<T>[];
  rowHeight?: number;
  headerRowCount?: number;
  defaultSortState?: DefaultSortState<T>[];
  defaultFilterState?: DefaultFilterState<T>[];
  minWidth?: number;
  title?: string;
  newPath?: string;
  newOnClick?: VoidFunction;
  CTA?: JSX.Element;
  rowOnClick?: (row: T, e: React.MouseEvent) => void;
  rowHref?: (row: T) => string | undefined | null;
  height?: number;
  type?: 'simple' | 'default';
  LeftMobileElement?: JSX.Element;
  paddingLeft?: number;
  paddingRight?: number;
  disableTableControlsHeader?: boolean;
  /** When true, hides the bottom footer (pagination controls or items count). Useful for embedded small tables. */
  hideFooter?: boolean;
  pagination?: Pagination;
  clearFilters?: boolean;
  filtersLoading?: boolean;
  /** When true (e.g. non‑paginated tables), shows skeleton rows and filter skeleton while data is loading. */
  isLoading?: boolean;
  /** Optional ref to receive { setFilterState } so the parent can trigger filter changes (e.g. from stats cards). */
  filterStateApiRef?: React.MutableRefObject<{ setFilterState: (f: FilterState[]) => void } | null>;
  /** Called after a bulk edit completes (e.g. to refresh counts). */
  onBulkEditComplete?: () => void;
  /** Optional ref to receive a refresh function so the parent can refetch current page data (e.g. after single-row updates). */
  paginationRefreshRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  /** Per-row background when not bulk-selected (e.g. draft add row). Bulk selection styling still wins. */
  getRowBackground?: (row: T) => RowBackgroundSurface | undefined;
  /** With bulk edit: rows returning false use a disabled checkbox and are skipped by select-all / shift-range. */
  isRowBulkSelectable?: (row: T) => boolean;
  /**
   * Rows matching this predicate always sort to the bottom of the list for every active sort key,
   * regardless of ascending/descending direction (e.g. draft “add row” in an inline-edited table).
   */
  pinRowsToBottom?: (row: T) => boolean;
  /**
   * When true, the table body height follows row content (no viewport-based min height, no large bottom padding).
   * Use for embedded lists like Smart Pantry. Width is still driven by `minWidth` + horizontal scroll when needed.
   */
  shrinkToContent?: boolean;
}

const NewTable = <T extends RowType>({
  columns,
  headerRowCount = 1,
  defaultSortState = [],
  defaultFilterState = [],
  minWidth,
  rowHeight = CELL_HEIGHT,
  newPath,
  newOnClick,
  title,
  CTA,
  rowOnClick,
  rowHref,
  type = 'default',
  disableTableControlsHeader = false,
  hideFooter = false,
  pagination,
  nonPaginatedRows,
  clearFilters = false,
  filtersLoading = false,
  isLoading: isLoadingProp = false,
  filterStateApiRef,
  onBulkEditComplete,
  paginationRefreshRef,
  getRowBackground,
  isRowBulkSelectable,
  pinRowsToBottom,
  shrinkToContent = false,
  ...props
}: Props<T>) => {
  const history = useHistory();
  const columnWidthsRef = useRef<ColumnWidths>({});
  const handleSetFilterStateRef = useRef<(f: FilterState[]) => Promise<void>>(() => Promise.resolve());
  const { windowHeight, appPaddingX } = useContext(app);
  const paddingLeft = props.paddingLeft ?? appPaddingX;
  const paddingRight = props.paddingRight ?? appPaddingX;
  const [renderOrdinal, setRenderOrdinal] = useState(0);
  const [backendRows, setBackendRows] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(!!pagination);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { xs, sm } = useContext(media);
  const isMobile = xs || sm;
  const { sort, filters } = qs.parse(history.location.search, { ignoreQueryPrefix: true });
  const sortParams: SortState[] = sort ? (JSON.parse(sort as string) as SortState[]) : [];
  const filterParams: FilterState[] = filters
    ? (JSON.parse(filters as string) as FilterState[]).map(filter => {
        if (filter.endDate) filter.endDate = new Date(filter.endDate);
        if (filter.startDate) filter.startDate = new Date(filter.startDate);
        if (filter.onDate) filter.onDate = new Date(filter.onDate);
        if (filter.beforeDate) filter.beforeDate = new Date(filter.beforeDate);
        if (filter.afterDate) filter.afterDate = new Date(filter.afterDate);
        return filter;
      })
    : [];
  const [sortState, setSortState] = useState<SortState[]>(
    sortParams.length > 0 ? sortParams : (defaultSortState as SortState[])
  );
  const [filterState, setFilterState] = useState<FilterState[]>(
    filterParams.length > 0 ? filterParams : (defaultFilterState as FilterState[])
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(pagination?.defaultPageSize ?? 10);
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
  const lastSelectedRowIndexRef = useRef<number | null>(null);

  const filterStateRef = useRef(filterState);
  filterStateRef.current = filterState;
  const sortStateRef = useRef(sortState);
  sortStateRef.current = sortState;
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const pageSizeRef = useRef(pageSize);
  pageSizeRef.current = pageSize;

  // Effect to handle initial data fetch when pagination is enabled
  useEffect(() => {
    const fetchInitialData = async () => {
      if (pagination) {
        setIsLoading(true);
        try {
          const data = await pagination.onFilterAndSortStateChange?.({
            filterState,
            sortState,
            page: 1,
            pageSize
          });
          if (data) {
            setTotalCount(data.total);
            setBackendRows(data.items);
          }
        } finally {
          setIsLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [tableHeight, setTableHeight] = useState<number>();

  // Filter out hidden columns only for rendering
  const visibleColumns = columns.filter(column => !column.hide);

  const rows = sortRows({
    rows: filterRows({ rows: nonPaginatedRows || [], filterState, columns }),
    columns,
    sortState,
    pinRowsToBottom
  });

  useEffect(() => {
    if (type === 'simple') return;

    if (xs) {
      lockScreenScroll();
    }

    return () => {
      if (xs) {
        unlockScreenScroll();
      }
    };
  }, [xs, type]);

  const setColumnWidths: SetColumnWidths = columnWidths => {
    columnWidthsRef.current = { ...columnWidthsRef.current, ...columnWidths };

    // if all column widths are present, increment renderOrdinal
    if (Object.values(columnWidthsRef.current).every(v => v !== undefined) && renderOrdinal === 0) {
      setRenderOrdinal(1);
    }
  };

  const handleSetFilterState = async (filterState: FilterState[]) => {
    setFilterState(filterState);
    setCurrentPage(1); // Reset to first page
    const filterParams = new URLSearchParams({ filters: JSON.stringify(filterState) });
    const searchParams = new URLSearchParams({ sort: JSON.stringify(sortState) });
    history.replace({
      pathname: history.location.pathname,
      search: `${filterParams.toString()}&${searchParams.toString()}`
    });

    if (pagination) {
      setIsLoading(true);
      try {
        const data = await pagination.onFilterAndSortStateChange?.({
          filterState,
          sortState,
          page: 1,
          pageSize
        });
        if (data) {
          setTotalCount(data.total);
          setBackendRows(data.items);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  handleSetFilterStateRef.current = handleSetFilterState;

  useEffect(() => {
    if (filterStateApiRef) {
      filterStateApiRef.current = {
        setFilterState: (f: FilterState[]) => handleSetFilterStateRef.current(f)
      };
    }
    return () => {
      if (filterStateApiRef) filterStateApiRef.current = null;
    };
  }, [filterStateApiRef]);

  const handleSetSortState = async (sortState: SortState[]) => {
    setSortState(sortState);
    setCurrentPage(1); // Reset to first page
    const filterParams = new URLSearchParams({ filters: JSON.stringify(filterState) });
    const searchParams = new URLSearchParams({ sort: JSON.stringify(sortState) });
    history.replace({
      pathname: history.location.pathname,
      search: `${filterParams.toString()}&${searchParams.toString()}`
    });

    if (pagination) {
      setIsLoading(true);
      try {
        const data = await pagination.onFilterAndSortStateChange?.({
          filterState,
          sortState,
          page: 1,
          pageSize
        });
        if (data) {
          setTotalCount(data.total);
          setBackendRows(data.items);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePageSizeChange = async (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    setIsLoading(true);
    try {
      const data = await pagination?.onPageSizeChange?.({
        filterState,
        sortState,
        page: 1,
        pageSize: newPageSize
      });
      if (data) {
        setTotalCount(data.total);
        setBackendRows(data.items);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = async (newPage: number) => {
    setCurrentPage(newPage);
    if (pagination) {
      setIsLoading(true);
      try {
        const data = await pagination.onPageChange?.({
          filterState,
          sortState,
          page: newPage,
          pageSize
        });
        if (data) {
          setTotalCount(data.total);
          setBackendRows(data.items);
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  const refreshData = useCallback(async () => {
    if (!pagination) return;
    setIsLoading(true);
    try {
      const data = await pagination.onFilterAndSortStateChange?.({
        filterState,
        sortState,
        page: currentPage,
        pageSize
      });
      if (data) {
        setTotalCount(data.total);
        setBackendRows(data.items);
      }
    } finally {
      setIsLoading(false);
    }
  }, [pagination, filterState, sortState, currentPage, pageSize]);

  useEffect(() => {
    if (paginationRefreshRef) {
      paginationRefreshRef.current = refreshData;
      return () => {
        paginationRefreshRef.current = null;
      };
    }
  }, [paginationRefreshRef, refreshData]);

  const isDefaultType = type === 'default';

  const headerIndices = new Array(headerRowCount).fill(0).map((_, index) => index);

  const loading = pagination ? isLoading : isLoadingProp;
  const displayRows = pagination && backendRows ? backendRows : rows || [];
  const showSkeletonRows = loading && displayRows.length === 0;
  const showFiltersSkeleton = filtersLoading || (loading && !hasLoadedOnce);
  const SKELETON_ROW_COUNT = 8;

  const hasBulkEdit = visibleColumns.some(col => col.bulkEdit);
  const canBulkSelectRow = isRowBulkSelectable ?? (() => true);
  const bulkSelectableRows = hasBulkEdit ? displayRows.filter(row => canBulkSelectRow(row)) : [];
  const selectedBulkSelectableCount = bulkSelectableRows.filter(row => selectedIds.has(row.id)).length;

  const bulkSelectHeaderSelectAll =
    hasBulkEdit && bulkSelectableRows.length > 0
      ? {
          value:
            selectedBulkSelectableCount === 0
              ? ('off' as const)
              : selectedBulkSelectableCount === bulkSelectableRows.length
                ? ('checked' as const)
                : ('indeterminate' as const),
          onToggle: (selected: boolean) => {
            if (selected) {
              setSelectedIds(new Set(bulkSelectableRows.map(r => r.id)));
            } else {
              setSelectedIds(new Set());
            }
          }
        }
      : undefined;

  const displayColumns = hasBulkEdit
    ? [
        {
          key: BULK_SELECT_COLUMN_KEY,
          header: '',
          cellContentType: 'check' as const,
          headerSelectAll: bulkSelectHeaderSelectAll,
          minWidth: 52,
          width: 52
        } as Column<T>,
        ...visibleColumns
      ]
    : visibleColumns;

  const handleRowSelectToggle = (rowId: number | string, selected: boolean, rowIndex?: number, shiftKey?: boolean) => {
    const index = rowIndex ?? displayRows.findIndex(r => r.id === rowId);
    if (index < 0) {
      return;
    }

    const toggledRow = displayRows[index];
    if (toggledRow && isRowBulkSelectable && !isRowBulkSelectable(toggledRow)) {
      return;
    }

    if (shiftKey && lastSelectedRowIndexRef.current !== null) {
      const start = Math.min(lastSelectedRowIndexRef.current, index);
      const end = Math.max(lastSelectedRowIndexRef.current, index);
      setSelectedIds(prev => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          const r = displayRows[i];
          if (!r) {
            continue;
          }
          if (isRowBulkSelectable && !isRowBulkSelectable(r)) {
            continue;
          }
          const id = r.id;
          if (id != null) {
            if (selected) {
              next.add(id);
            } else {
              next.delete(id);
            }
          }
        }
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (selected) {
          next.add(rowId);
        } else {
          next.delete(rowId);
        }
        return next;
      });
    }
    lastSelectedRowIndexRef.current = index;
  };

  const columnWidths = displayColumns.reduce<ColumnWidths>((widths, column) => {
    widths[column.key as string] = columnWidthsRef.current[column.key as string];

    return widths;
  }, {});

  const totalColumnWidth = Object.values(columnWidths).reduce((sum, width) => (sum || 0) + (width || 0), 0);

  return (
    <Layout
      flexGrow={shrinkToContent ? 0 : 1}
      direction="column"
      width="100%"
      data-cy="new-table"
      overflow={isMobile ? undefined : 'hidden'}
    >
      {isDefaultType && (!!title || !!newPath || !!newOnClick || !!CTA) && (
        <Layout
          paddingX={paddingLeft}
          align="center"
          justify="space-between"
          {...(isMobile ? { height: TABLE_TITLE_HEIGHT_MOBILE } : { paddingTop: 24, paddingBottom: 16 })}
        >
          <Layout align="center" height={44}>
            {title && (
              <Text size="h5" ellipsis>
                {title}
              </Text>
            )}
          </Layout>
          <Layout gap={8} align="center">
            {CTA}
            {(newPath || newOnClick) &&
              (isMobile ? (
                <IconButton
                  size="lg"
                  name="plus"
                  type="noBackground"
                  onClick={
                    newOnClick ||
                    (() => {
                      window.open(newPath, '_blank');
                    })
                  }
                />
              ) : (
                <Button
                  type="primary"
                  href={newPath}
                  size="xxs"
                  icon="plus"
                  iconPosition="left"
                  onClick={newOnClick}
                  text="Add"
                />
              ))}
          </Layout>
        </Layout>
      )}
      <Layout
        {...(isDefaultType ? { marginTop: isMobile ? undefined : 12, paddingLeft: paddingLeft } : {})}
        onMeasure={({ y }) => {
          if (shrinkToContent) {
            return;
          }
          if (type === 'simple') {
            setTableHeight(props.height || y);
          } else {
            setTableHeight(
              windowHeight - y - (isMobile ? BOTTOM_NAVIGATION_HEIGHT + 12 : !disableTableControlsHeader ? 98 : 0) // 98 harcoded value because i can't figure out how to get the height of the table controls header container
            );
          }
        }}
      />
      {!disableTableControlsHeader && (
        <Layout data-cy="table-controls-header">
          {showFiltersSkeleton ? (
            <Layout
              paddingX={xs ? 24 : 40}
              paddingY={12}
              direction="row"
              gap={12}
              position="sticky"
              top={0}
              color="white"
              zIndex={1000}
              align="center"
              __style={{ flexWrap: 'wrap' }}
            >
              {xs ? (
                <Layout flexGrow={1}>
                  <Skeleton height={44} borderRadius={8} width="100%" />
                </Layout>
              ) : (
                <>
                  <Layout direction="row" gap={8} __style={{ flexWrap: 'wrap' }}>
                    <Skeleton height={32} borderRadius={8} width={275} />
                    <Skeleton height={32} borderRadius={8} width={140} />
                    <Skeleton height={32} borderRadius={8} width={140} />
                    <Skeleton height={32} borderRadius={8} width={140} />
                    <Skeleton height={32} borderRadius={8} width={140} />
                  </Layout>
                  {clearFilters && (
                    <Layout __style={{ marginLeft: 'auto' }}>
                      <Skeleton height={32} borderRadius={8} width={100} />
                    </Layout>
                  )}
                </>
              )}
            </Layout>
          ) : (
            <TableControlsHeader
              width={totalColumnWidth}
              columns={columns as any}
              rows={pagination ? backendRows : rows}
              filterState={filterState}
              sortState={sortState}
              setFilterState={handleSetFilterState}
              clearFilters={clearFilters}
            />
          )}
          {!isMobile && <Layout marginX={40} __style={{ borderBottom: `2px solid ${GRAY_50_HEX}` }} />}
        </Layout>
      )}
      <Layout
        {...(shrinkToContent
          ? {}
          : xs
            ? {
                height: `calc(100dvh - ${
                  TABLE_TITLE_HEIGHT_MOBILE + TABLE_FILTER_SORT_HEIGHT_MOBILE + BOTTOM_NAVIGATION_HEIGHT
                }px)`
              }
            : { height: tableHeight ?? 400 })}
        {...(isDefaultType ? { marginLeft: paddingLeft, paddingRight: paddingRight } : {})}
        {...(shrinkToContent ? { flexGrow: 0, width: '100%', minWidth: 0 } : { flex: true })}
        overflow="auto"
      >
        <NewTableAutoSizer shrinkToContent={shrinkToContent}>
          {({ width }: { width: number }) => (
            <>
              {/* the padding bottom value should below should technically be set to the height of the controls header + the footer height*/}
              <div
                style={{
                  width: width,
                  minWidth,
                  paddingBottom: shrinkToContent ? 0 : 162
                }}
              >
                {headerIndices.map(index => (
                  <HeaderRow
                    disableTableControlsHeader={disableTableControlsHeader}
                    getColumnWidth={(key: string) => {
                      return columnWidthsRef.current[key];
                    }}
                    setColumnWidths={setColumnWidths!}
                    columns={displayColumns}
                    key={index}
                    sortState={sortState!}
                    setSortState={handleSetSortState}
                    filterState={filterState!}
                    setFilterState={handleSetFilterState}
                    style={{
                      left: 0,
                      width: '100%',
                      minWidth: 'fit-content',
                      height: NEW_TABLE_HEADER_HEIGHT
                    }}
                  />
                ))}
                {showSkeletonRows
                  ? Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
                      <div
                        key={`skeleton-row-${rowIndex}`}
                        className="Table-row"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          left: 0,
                          width: '100%',
                          minWidth: 'fit-content',
                          height: rowHeight
                        }}
                      >
                        {displayColumns.map(
                          ({
                            key,
                            width: colWidth,
                            minWidth: colMinWidth,
                            skeletonLeadingImage,
                            skeletonLeadingAvatar
                          }) => {
                            const width =
                              columnWidthsRef.current[key] ??
                              (typeof colWidth === 'number' ? colWidth : colMinWidth) ??
                              120;
                            const SKELETON_IMAGE_SIZE = 36;
                            const SKELETON_AVATAR_SIZE = 24;
                            const SKELETON_LEADING_TEXT_GAP = 12;
                            const hasLeadingVisual = skeletonLeadingImage || skeletonLeadingAvatar;
                            const leadingSize = skeletonLeadingImage
                              ? SKELETON_IMAGE_SIZE
                              : skeletonLeadingAvatar
                                ? SKELETON_AVATAR_SIZE
                                : 0;
                            return (
                              <Layout
                                key={key as string}
                                paddingX={CELL_PADDING}
                                align="center"
                                __style={{ width, minWidth: width }}
                              >
                                {hasLeadingVisual ? (
                                  <Layout
                                    direction="row"
                                    gap={SKELETON_LEADING_TEXT_GAP}
                                    align="center"
                                    minWidth={0}
                                    width="100%"
                                  >
                                    <Skeleton
                                      variant={skeletonLeadingAvatar ? 'circle' : 'rect'}
                                      height={leadingSize}
                                      borderRadius={skeletonLeadingAvatar ? undefined : 8}
                                      width={leadingSize}
                                    />
                                    <Layout flexGrow={1} minWidth={0}>
                                      <Skeleton height={20} borderRadius={8} width="100%" />
                                    </Layout>
                                  </Layout>
                                ) : (
                                  <Skeleton height={20} borderRadius={8} width="100%" />
                                )}
                              </Layout>
                            );
                          }
                        )}
                      </div>
                    ))
                  : displayRows.map((row, index) => (
                      <Row
                        key={`${row.id}-${renderOrdinal}`}
                        style={{ left: 0, width: '100%', height: rowHeight }}
                        data={{
                          columns: displayColumns,
                          row,
                          rowIndex: index,
                          bulkSelectColumnKey: hasBulkEdit ? BULK_SELECT_COLUMN_KEY : undefined,
                          selectedIds,
                          onRowSelectToggle: handleRowSelectToggle,
                          getRowBackground,
                          isRowBulkSelectable
                        }}
                        index={index}
                        getColumnWidth={(key: string) => {
                          return columnWidthsRef.current[key];
                        }}
                        rowOnClick={rowOnClick}
                        rowHref={rowHref}
                      />
                    ))}
                {!hideFooter && (
                  <TableFooter
                    pageSize={pageSize}
                    handlePageSizeChange={handlePageSizeChange}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    rows={pagination ? backendRows : rows}
                    pagination={pagination}
                    totalCount={totalCount}
                  />
                )}
              </div>
            </>
          )}
        </NewTableAutoSizer>
      </Layout>
      {hasBulkEdit && selectedIds.size > 0 && (
        <FloatingBulkActions
          selectedCount={selectedIds.size}
          bulkEditColumns={visibleColumns.filter(
            (col): col is typeof col & { bulkEdit: NonNullable<typeof col.bulkEdit> } =>
              !!col.bulkEdit && !!col.bulkEditOptions
          )}
          selectedIds={selectedIds}
          selectedRows={displayRows.filter(row => selectedIds.has(row.id)) as any}
          onBulkEditComplete={async () => {
            setSelectedIds(new Set());
            if (pagination) {
              setIsLoading(true);
              try {
                const data = await pagination.onFilterAndSortStateChange?.({
                  filterState,
                  sortState,
                  page: currentPage,
                  pageSize
                });
                if (data) {
                  setTotalCount(data.total);
                  setBackendRows(data.items);
                }
              } finally {
                setIsLoading(false);
              }
            }
            onBulkEditComplete?.();
          }}
          onDismiss={() => {
            setSelectedIds(new Set());
          }}
        />
      )}
    </Layout>
  );
};

export default NewTable;
